import { isSensitiveKey, redactForDisplay, redactedJson, type RedactionOptions } from '../lib/redaction';

export type ConfigJsonPrimitive = string | number | boolean | null;
export type ConfigJsonValue = ConfigJsonPrimitive | ConfigJsonValue[] | { [key: string]: ConfigJsonValue };
export type ConfigDraftSource = 'draft' | 'published' | 'current' | 'empty';

export type ConfigDraftInput<TValue> = {
  currentDraft?: TValue | null;
  published?: TValue | null;
  current?: TValue | null;
  empty: TValue;
};

export type ResolvedConfigDraft<TValue> = {
  value: TValue;
  source: ConfigDraftSource;
  publishedValue: TValue;
};

export type ConfigDraftState<TValue> = {
  baseline: TValue;
  values: TValue;
  source: ConfigDraftSource;
  publishedValue: TValue;
};

export type JsonDiffKind = 'added' | 'removed' | 'changed';

export type JsonDiffRow = {
  path: string;
  kind: JsonDiffKind;
  before: string;
  after: string;
};

const missingValue = Symbol('missing');
type MissingValue = typeof missingValue;

export function resolveConfigDraftValue<TValue>(input: ConfigDraftInput<TValue>): ResolvedConfigDraft<TValue> {
  const publishedValue = cloneConfigValue(input.published ?? input.current ?? input.empty);
  if (input.currentDraft !== undefined && input.currentDraft !== null) {
    return { value: cloneConfigValue(input.currentDraft), source: 'draft', publishedValue };
  }
  if (input.published !== undefined && input.published !== null) {
    return { value: cloneConfigValue(input.published), source: 'published', publishedValue };
  }
  if (input.current !== undefined && input.current !== null) {
    return { value: cloneConfigValue(input.current), source: 'current', publishedValue };
  }
  return { value: cloneConfigValue(input.empty), source: 'empty', publishedValue };
}

export function createConfigDraftState<TValue>(input: ConfigDraftInput<TValue>): ConfigDraftState<TValue> {
  const resolved = resolveConfigDraftValue(input);
  return {
    baseline: cloneConfigValue(resolved.value),
    values: cloneConfigValue(resolved.value),
    source: resolved.source,
    publishedValue: cloneConfigValue(resolved.publishedValue),
  };
}

export function updateConfigDraftValues<TValue>(state: ConfigDraftState<TValue>, values: TValue): ConfigDraftState<TValue> {
  return { ...state, values: cloneConfigValue(values) };
}

export function updateConfigDraftField<TValue extends Record<string, unknown>, TKey extends keyof TValue>(
  state: ConfigDraftState<TValue>,
  field: TKey,
  value: TValue[TKey],
): ConfigDraftState<TValue> {
  return {
    ...state,
    values: {
      ...state.values,
      [field]: cloneConfigValue(value),
    },
  };
}

export function discardConfigDraftChanges<TValue>(state: ConfigDraftState<TValue>): ConfigDraftState<TValue> {
  return { ...state, values: cloneConfigValue(state.baseline) };
}

export function commitConfigDraftSave<TValue>(state: ConfigDraftState<TValue>, savedValues = state.values): ConfigDraftState<TValue> {
  const committed = cloneConfigValue(savedValues);
  return {
    ...state,
    baseline: cloneConfigValue(committed),
    values: cloneConfigValue(committed),
    source: 'draft',
  };
}

export function isConfigDraftDirty<TValue>(state: ConfigDraftState<TValue>) {
  return !configValuesEqual(state.values, state.baseline);
}

export function configValuesEqual(left: unknown, right: unknown) {
  return stableConfigStringify(left) === stableConfigStringify(right);
}

export function cloneConfigValue<TValue>(value: TValue): TValue {
  if (value === undefined || value === null) return value;
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch {
      return JSON.parse(JSON.stringify(value)) as TValue;
    }
  }
  return JSON.parse(JSON.stringify(value)) as TValue;
}

export function stableConfigStringify(value: unknown) {
  return JSON.stringify(normalizeConfigValue(value));
}

export function prettyConfigJson(value: unknown) {
  return JSON.stringify(normalizeConfigValue(value), null, 2);
}

export function redactedPrettyConfigJson(value: unknown, options: RedactionOptions = {}) {
  return redactedJson(normalizeConfigValue(value), options);
}

export function diffJsonValues(before: unknown, after: unknown, options: RedactionOptions = {}): JsonDiffRow[] {
  return collectDiffRows('$', before, after, false, false, undefined, options);
}

function collectDiffRows(
  path: string,
  before: unknown | MissingValue,
  after: unknown | MissingValue,
  beforeMissing: boolean,
  afterMissing: boolean,
  key?: string,
  options: RedactionOptions = {},
): JsonDiffRow[] {
  if (!beforeMissing && !afterMissing && configValuesEqual(before, after)) return [];
  if (isSensitiveKey(key)) return [diffRow(path, before, after, beforeMissing, afterMissing, key, options)];

  if (!beforeMissing && !afterMissing && isPlainObject(before) && isPlainObject(after)) {
    const childKeys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();
    return childKeys.flatMap(childKey => collectDiffRows(
      `${path}.${childKey}`,
      Object.prototype.hasOwnProperty.call(before, childKey) ? before[childKey] : missingValue,
      Object.prototype.hasOwnProperty.call(after, childKey) ? after[childKey] : missingValue,
      !Object.prototype.hasOwnProperty.call(before, childKey),
      !Object.prototype.hasOwnProperty.call(after, childKey),
      childKey,
      options,
    ));
  }

  if (!beforeMissing && !afterMissing && Array.isArray(before) && Array.isArray(after)) {
    const longestLength = Math.max(before.length, after.length);
    return Array.from({ length: longestLength }, (_, index) => collectDiffRows(
      `${path}[${index}]`,
      index in before ? before[index] : missingValue,
      index in after ? after[index] : missingValue,
      !(index in before),
      !(index in after),
      key,
      options,
    )).flat();
  }

  return [diffRow(path, before, after, beforeMissing, afterMissing, key, options)];
}

function diffRow(path: string, before: unknown | MissingValue, after: unknown | MissingValue, beforeMissing: boolean, afterMissing: boolean, key?: string, options: RedactionOptions = {}): JsonDiffRow {
  return {
    path,
    kind: beforeMissing ? 'added' : afterMissing ? 'removed' : 'changed',
    before: formatDiffValue(before, beforeMissing, key, options),
    after: formatDiffValue(after, afterMissing, key, options),
  };
}

function formatDiffValue(value: unknown | MissingValue, missing: boolean, key?: string, options: RedactionOptions = {}) {
  if (missing || value === missingValue) return '∅';
  const displayValue = redactForDisplay(value, key, options);
  if (typeof displayValue === 'string') return displayValue;
  if (displayValue === undefined) return 'undefined';
  return prettyConfigJson(displayValue);
}

function normalizeConfigValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(item => normalizeConfigValue(item));
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, childValue]) => childValue !== undefined)
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([childKey, childValue]) => [childKey, normalizeConfigValue(childValue)]),
    );
  }
  return value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
